import { useState } from "react";
import { toast } from "sonner";
import { Building2, Users, UserCog, Globe, HeartPulse, Crown, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader, StatCard, Panel, Field, Empty } from "@/components/common";
import { ClinicForm, emptyClinic } from "@/components/ClinicForm";
import { PersonForm, emptyPerson } from "@/components/PersonForm";
import { api, errMsg, fmtDate, useLoad } from "@/lib/api";

const Contact = ({ p }) => p ? (
  <div className="leading-tight"><p className="font-medium text-slate-800">{p.name}</p><p className="text-xs text-slate-500">{p.email}</p><p className="text-xs text-slate-500">{p.phone || "—"}</p></div>
) : <span className="text-xs text-slate-400">Not appointed</span>;

function CreateCeo({ onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ ...emptyPerson, organization_name: "" });
  const save = async () => {
    try { await api.post("/admin/ceos", f); toast.success("CEO account created"); setOpen(false); setF({ ...emptyPerson, organization_name: "" }); onDone(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <>
      <Button variant="outline" className="rounded-full" onClick={() => setOpen(true)} data-testid="superadmin-create-ceo-button"><Crown className="mr-2 h-4 w-4" />New CEO</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>Create CEO / MD account</DialogTitle></DialogHeader>
          <PersonForm value={f} onChange={setF} prefix="create-ceo" />
          <Field label="Organisation"><Input value={f.organization_name} onChange={(e) => setF({ ...f, organization_name: e.target.value })} data-testid="create-ceo-org-input" /></Field>
          <DialogFooter><Button onClick={save} data-testid="create-ceo-submit-button">Create CEO</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function CreateClinic({ ceos, onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(emptyClinic);
  const [ceo, setCeo] = useState("");
  const save = async () => {
    if (!ceo) return toast.error("Select a CEO");
    try { await api.post("/admin/clinics", { ...f, ceo_id: ceo }); toast.success("Clinic created"); setOpen(false); setF(emptyClinic); onDone(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <>
      <Button className="rounded-full" onClick={() => setOpen(true)} data-testid="superadmin-create-clinic-modal-trigger"><Plus className="mr-2 h-4 w-4" />New clinic</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Create clinic for a CEO</DialogTitle></DialogHeader>
          <Field label="Owner (CEO / MD)">
            <Select value={ceo} onValueChange={setCeo}>
              <SelectTrigger data-testid="create-clinic-ceo-select"><SelectValue placeholder="Select CEO" /></SelectTrigger>
              <SelectContent>{ceos.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} · {c.email}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <ClinicForm value={f} onChange={setF} />
          <DialogFooter><Button onClick={save} data-testid="create-clinic-submit-button">Create clinic</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function AdminDashboard() {
  const [data, reload] = useLoad("/admin/overview");
  const s = data?.stats || {};
  const toggle = async (id, active) => {
    try { await api.patch(`/admin/users/${id}/status`, { active }); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <>
      <PageHeader eyebrow="Platform control" title="Super Admin" subtitle="Every clinic group on the platform, with direct contacts for CEOs and their managers.">
        <CreateCeo onDone={reload} />
        <CreateClinic ceos={data?.ceos || []} onDone={reload} />
      </PageHeader>
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
        <StatCard label="Clinics" value={s.clinics ?? "—"} icon={Building2} testId="admin-stat-clinics" />
        <StatCard label="CEOs" value={s.ceos ?? "—"} icon={Crown} tone="amber" testId="admin-stat-ceos" delay={50} />
        <StatCard label="Managers" value={s.managers ?? "—"} icon={UserCog} tone="indigo" testId="admin-stat-managers" delay={100} />
        <StatCard label="Staff" value={s.staff ?? "—"} icon={Users} tone="green" testId="admin-stat-staff" delay={150} />
        <StatCard label="Patients" value={s.patients ?? "—"} icon={HeartPulse} tone="rose" testId="admin-stat-patients" delay={200} />
        <StatCard label="Countries" value={s.countries ?? "—"} icon={Globe} tone="slate" testId="admin-stat-countries" delay={250} />
      </div>
      <Tabs defaultValue="clinics">
        <TabsList><TabsTrigger value="clinics" data-testid="admin-tab-clinics">Clinics</TabsTrigger><TabsTrigger value="ceos" data-testid="admin-tab-ceos">CEOs</TabsTrigger></TabsList>
        <TabsContent value="clinics">
          <Panel testId="admin-clinics-table">
            {!data?.clinics?.length ? <Empty text="No clinics yet." /> : (
              <div className="overflow-x-auto"><Table>
                <TableHeader><TableRow><TableHead>Clinic</TableHead><TableHead>Location</TableHead><TableHead>CEO / MD</TableHead><TableHead>Manager</TableHead><TableHead>Staff</TableHead><TableHead>Created</TableHead></TableRow></TableHeader>
                <TableBody>{data.clinics.map((c) => (
                  <TableRow key={c.id} data-testid={`admin-clinic-row-${c.id}`}>
                    <TableCell><p className="font-semibold">{c.name}</p><p className="font-mono text-xs text-emerald-700">{c.currency}</p></TableCell>
                    <TableCell className="text-sm">{[c.city, c.country].filter(Boolean).join(", ")}</TableCell>
                    <TableCell><Contact p={c.ceo} /></TableCell>
                    <TableCell><Contact p={c.manager} /></TableCell>
                    <TableCell>{c.staff_count}</TableCell>
                    <TableCell className="text-sm text-slate-500">{fmtDate(c.created_at)}</TableCell>
                  </TableRow>))}
                </TableBody></Table></div>)}
          </Panel>
        </TabsContent>
        <TabsContent value="ceos">
          <Panel testId="admin-ceos-table">
            {!data?.ceos?.length ? <Empty text="No CEOs registered yet." /> : (
              <div className="overflow-x-auto"><Table>
                <TableHeader><TableRow><TableHead>CEO / MD</TableHead><TableHead>Organisation</TableHead><TableHead>Phone</TableHead><TableHead>Clinics</TableHead><TableHead>Joined</TableHead><TableHead>Active</TableHead></TableRow></TableHeader>
                <TableBody>{data.ceos.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell><p className="font-semibold">{c.name}</p><p className="text-xs text-slate-500">{c.email}</p></TableCell>
                    <TableCell>{c.organization_name || "—"}</TableCell>
                    <TableCell>{c.phone || "—"}</TableCell>
                    <TableCell>{c.clinic_count}</TableCell>
                    <TableCell className="text-sm text-slate-500">{fmtDate(c.created_at)}</TableCell>
                    <TableCell><Switch checked={c.active} onCheckedChange={(v) => toggle(c.id, v)} data-testid={`admin-ceo-active-${c.id}`} /></TableCell>
                  </TableRow>))}
                </TableBody></Table></div>)}
          </Panel>
        </TabsContent>
      </Tabs>
    </>
  );
}
